import type { BidFormData } from "@/pages/bid-calculator/components/BidForm";
import { getDefaultBidFormData } from "@/pages/bid-calculator/components/BidForm";
import type { ReverseFormData } from "@/pages/bid-calculator/components/ReverseForm";
import { getDefaultReverseFormData } from "@/pages/bid-calculator/components/ReverseForm";

interface AuctionItemLike {
  id: string;
  propertyType: string;
  appraisedValue: number;
  minimumPrice: number;
  /** 시세 추정이 없는 물건은 null 이다 — 감정가로 대체하지 않는다. */
  expectedSalePrice: number | null;
  expectedProfit: number | null;
  hasTenant: boolean;
  exclusiveArea?: number | null;
  buildingAge?: number | null;
}

/**
 * 시세·수익 추정이 없을 때 계산기에 넣을 값.
 * 0 을 넣으면 계산기가 "시세 0원" 으로 돌아 결과가 음수로 깨진다.
 * 최저가를 시세 자리에 두어 사용자가 직접 고쳐 쓰게 한다(감정가는 착시라 쓰지 않는다).
 */
function fallbackMarketPrice(item: AuctionItemLike): number {
  return item.expectedSalePrice ?? item.minimumPrice;
}

/** 경매 물건 타입명 → 계산기 폼 타입명 정규화 */
function normalizePropertyType(type: string): string {
  if (type === "다세대") return "다세대/연립";
  return type;
}

/** 임차인 여부 → 점유자 유형 */
function getOccupantType(hasTenant: boolean): string {
  return hasTenant ? "tenant_no_rights" : "없음";
}

/**
 * 경매 물건 데이터 → 일반방식 BidFormData
 * estimatedMarketPrice = expectedSalePrice (급매 시세 추정)
 */
export function auctionToNormalBidData(item: AuctionItemLike): BidFormData {
  const defaults = getDefaultBidFormData();
  return {
    ...defaults,
    propertyType: normalizePropertyType(item.propertyType),
    exclusiveArea: item.exclusiveArea ?? 85,
    buildingAge: item.buildingAge ?? 15,
    estimatedMarketPrice: fallbackMarketPrice(item),
    takeoverAmount: 0,
    occupantType: getOccupantType(item.hasTenant),
    targetProfit: item.expectedProfit ?? defaults.targetProfit,
  };
}

/**
 * 경매 물건 데이터 → 시세역산방식 ReverseFormData
 * acuteMarketPrice = expectedSalePrice (급매가 추정)
 * courtAppraisalPrice = appraisedValue
 * minimumBidPrice = minimumPrice
 */
export function auctionToReverseBidData(item: AuctionItemLike): ReverseFormData {
  const defaults = getDefaultReverseFormData();
  const market = fallbackMarketPrice(item);
  const roi = market > 0 && item.expectedProfit != null
    ? Math.round((item.expectedProfit / market) * 100)
    : 15;
  return {
    ...defaults,
    propertyType: normalizePropertyType(item.propertyType),
    exclusiveArea: item.exclusiveArea ?? 85,
    buildingAge: item.buildingAge ?? 15,
    acuteMarketPrice: market,
    courtAppraisalPrice: item.appraisedValue,
    minimumBidPrice: item.minimumPrice,
    targetMargin: item.expectedProfit ?? defaults.targetMargin,
    targetROI: roi,
    marginMode: "amount",
    occupantType: getOccupantType(item.hasTenant),
    takeoverAmount: 0,
  };
}